import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Params, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import DateInput from "../../../../ui/components/date-input/date-input";
import { Convenio } from "../../../models/convenio";
import { convenioServices } from "../../../services/convenio.services";
import { getFormattedDate } from "../../../utils/common.utils";
import { TIPO_ANTIGUEDAD_OPTIONS } from "../../../utils/convenio.utils";
import { handle400Error } from "../../../utils/handle-400-errors";
import "./convenio-edit.scss";

interface ConvenioEditForm {
  nombre: string;
  fechaFin?: Date;
  active: boolean;
}

const schema = yup.object().shape({
  nombre: yup.string().required("Campo requerido"),
  fechaFin: yup.date().nullable(),
  active: yup.boolean().required(),
});

export default function ConvenioEdit() {
  const { id } = useParams<Params>();
  const navigate = useNavigate();
  const [convenio, setConvenio] = useState<Convenio>();
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  const {
    control,
    register,
    reset,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm<ConvenioEditForm>({
    mode: "all",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      nombre: "",
      active: true,
    },
  });

  const isActive = watch("active");

  useEffect(() => {
    if (!id) return;
    setFetching(true);
    convenioServices
      .getById(id)
      .then((response) => {
        const data: Convenio = response.data;
        setConvenio(data);
        reset({
          nombre: data.nombre,
          fechaFin: data.fechaFin ? new Date(data.fechaFin) : undefined,
          active: !!data.active,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [id, reset]);

  const onSubmit = handleSubmit((data) => {
    if (!id) return;
    setSaving(true);
    convenioServices
      .update(id, data)
      .then(() => {
        navigate("/convenios", {
          state: {
            textAlert: `El convenio ${data.nombre} fue actualizado correctamente.`,
          },
        });
      })
      .catch((error) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        }
      })
      .finally(() => setSaving(false));
  });

  const inputProps = { register, getFieldState, formState, errors };

  const tipoAntiguedadLabel = TIPO_ANTIGUEDAD_OPTIONS.find(
    (o) => o.value === convenio?.tipoAntiguedad
  )?.label;

  return (
    <div className="convenio-edit-container">
      <div className="convenio-edit-header">
        <button
          type="button"
          className="convenio-back-btn"
          onClick={() => navigate("/convenios")}
          title="Volver"
        >
          <ArrowBackIosNewOutlinedIcon fontSize="medium" />
        </button>
        <div className="convenio-edit-header__icon">
          <HandshakeOutlinedIcon />
        </div>
        <div className="convenio-edit-header__info">
          <Typography variant="h5" className="convenio-edit-header__title">
            Editar Convenio
          </Typography>
          {!fetching && convenio && (
            <span className="convenio-edit-header__subtitle">
              {convenio.obraSocialNombre}
            </span>
          )}
        </div>
        {!fetching && convenio && (
          <div className="convenio-edit-header__actions">
            <Chip
              label={isActive ? "ACTIVO" : "INACTIVO"}
              color={isActive ? "success" : "default"}
              size="small"
            />
            <Button
              variant="outlined"
              startIcon={<ListAltOutlinedIcon />}
              onClick={() => navigate(`/convenios/${id}/practicas`)}
            >
              Editar Prácticas
            </Button>
          </div>
        )}
      </div>

      {fetching || !convenio ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <section className="convenio-section">
            <div className="convenio-section__title">
              <InfoOutlinedIcon fontSize="small" />
              <span>Información del convenio</span>
            </div>
            <div className="convenio-section__body">
              <div className="convenio-info-grid">
                <div className="read-field">
                  <span className="read-field-label">Obra Social</span>
                  <span className="read-field-value">
                    {convenio.obraSocialNombre}
                  </span>
                </div>
                <div className="read-field">
                  <span className="read-field-label">Fecha inicio</span>
                  <span className="read-field-value">
                    {getFormattedDate(convenio.fechaInicio.toString())}
                  </span>
                </div>
                <div className="read-field">
                  <span className="read-field-label">
                    Considera antigüedad
                  </span>
                  <span className="read-field-value">
                    {convenio.consideraAntiguedad ? "Sí" : "No"}
                  </span>
                </div>
                {convenio.consideraAntiguedad && (
                  <>
                    <div className="read-field">
                      <span className="read-field-label">
                        Tipo de antigüedad
                      </span>
                      <span className="read-field-value">
                        {tipoAntiguedadLabel ?? "-"}
                      </span>
                    </div>
                    <div className="read-field">
                      <span className="read-field-label">
                        Fecha de recategorización
                      </span>
                      <span className="read-field-value">
                        {convenio.fechaRecategorizacion
                          ? getFormattedDate(
                              convenio.fechaRecategorizacion.toString()
                            )
                          : "-"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          <Box component="form" autoComplete="off" noValidate onSubmit={onSubmit}>
            <section className="convenio-section">
              <div className="convenio-section__title">
                <TuneOutlinedIcon fontSize="small" />
                <span>Datos editables</span>
              </div>
              <div className="convenio-section__body">
                <div className="row">
                  <CustomInput
                    name="nombre"
                    label="Nombre del convenio"
                    type="text"
                    required
                    maxLength={200}
                    value={convenio.nombre}
                    {...inputProps}
                  ></CustomInput>
                  <Controller
                    name="fechaFin"
                    control={control}
                    render={({ field }) => (
                      <DateInput
                        field={field}
                        name="fechaFin"
                        label="Fecha fin (opcional)"
                        value={field.value}
                        {...inputProps}
                      ></DateInput>
                    )}
                  />
                </div>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <label className="convenio-status-toggle">
                      <EventOutlinedIcon
                        fontSize="small"
                        className="convenio-status-toggle__icon"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={
                          field.value ? "Convenio activo" : "Convenio inactivo"
                        }
                      />
                    </label>
                  )}
                />
              </div>
            </section>

            <div className="row actions">
              <Button
                variant="outlined"
                onClick={() => navigate("/convenios")}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                type="submit"
                startIcon={!saving ? <SaveOutlinedIcon /> : undefined}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </Box>
        </>
      )}
    </div>
  );
}
